import { SupportFormData, FormErrors, ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '../types/form';

export const validateEmail = (email: string): string | undefined => {
  if (!email.trim()) {
    return 'Email address is required';
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  return undefined;
};

export const validateName = (name: string): string | undefined => {
  // Name is now optional - only validate if provided
  if (!name.trim()) {
    return undefined;
  }
  
  if (name.trim().length < 2) {
    return 'Name must be at least 2 characters';
  }
  
  // Allow any Unicode letters, spaces, hyphens, and apostrophes
  const nameRegex = /^[\p{L}\p{M}\s'\-\.]+$/u;
  if (!nameRegex.test(name)) {
    return 'Name contains invalid characters';
  }
  
  return undefined;
};

export const validateBin = (bin: string): string | undefined => {
  // BIN is now optional - only validate if provided
  if (!bin.trim()) {
    return undefined;
  }
  
  // Remove any spaces or non-digits
  const cleanBin = bin.replace(/\D/g, '');
  
  if (cleanBin.length !== 6 && cleanBin.length !== 8) {
    return 'BIN must be exactly 6 or 8 digits';
  }
  
  return undefined;
};

export const validateLast4 = (last4: string): string | undefined => {
  // Last 4 digits is now optional - only validate if provided
  if (!last4.trim()) {
    return undefined;
  }
  
  // Remove any spaces or non-digits
  const cleanLast4 = last4.replace(/\D/g, '');
  
  if (cleanLast4.length !== 4) {
    return 'Last 4 digits must be exactly 4 digits';
  }
  
  return undefined;
};

export const validatePaymentDate = (paymentDate: string): string | undefined => {
  // Payment date is now optional - only validate if provided
  if (!paymentDate.trim()) {
    return undefined;
  }
  
  const date = new Date(paymentDate);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  
  if (isNaN(date.getTime())) {
    return 'Please select a valid date';
  }
  
  if (date > today) {
    return 'Payment date cannot be in the future';
  }
  
  return undefined;
};

export const validateTransactionAmount = (amount: string): string | undefined => {
  // Transaction amount is now optional - only validate if provided
  if (!amount.trim()) {
    return undefined;
  }
  
  const numericAmount = parseFloat(amount);
  if (isNaN(numericAmount) || numericAmount < 0) {
    return 'Please enter a valid amount (0 or greater)';
  }
  
  return undefined;
};

export const validateApplicationTopic = (topic: string): string | undefined => {
  // Application topic is now optional - only validate if provided
  if (!topic.trim()) {
    return undefined;
  }
  
  return undefined;
};

export const validateMessage = (message: string): string | undefined => {
  // Message is now optional - only validate if provided
  if (!message.trim()) {
    return undefined;
  }
  
  if (message.trim().length < 10) {
    return 'Message must be at least 10 characters';
  }
  
  if (message.length > 1800) {
    return 'Message cannot exceed 1800 characters';
  }
  
  return undefined;
};

export const validateFile = (file: File | null): string | undefined => {
  if (!file) {
    return undefined; // File is optional
  }
  
  if (file.size > MAX_FILE_SIZE) {
    return 'File size cannot exceed 5MB';
  }
  
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return 'Only images (JPG, PNG), PDFs, and Word documents are allowed';
  }
  
  return undefined;
};

export const validateForm = (formData: SupportFormData): FormErrors => {
  const errors: FormErrors = {};
  
  // Only email is required - validate all fields only if they have content
  const emailError = validateEmail(formData.email);
  if (emailError) errors.email = emailError;
  
  const nameError = validateName(formData.name);
  if (nameError) errors.name = nameError;
  
  const binError = validateBin(formData.bin);
  if (binError) errors.bin = binError;
  
  const last4Error = validateLast4(formData.last4);
  if (last4Error) errors.last4 = last4Error;
  
  const paymentDateError = validatePaymentDate(formData.paymentDate);
  if (paymentDateError) errors.paymentDate = paymentDateError;
  
  const transactionAmountError = validateTransactionAmount(formData.transactionAmount);
  if (transactionAmountError) errors.transactionAmount = transactionAmountError;
  
  const applicationTopicError = validateApplicationTopic(formData.applicationTopic);
  if (applicationTopicError) errors.applicationTopic = applicationTopicError;
  
  const messageError = validateMessage(formData.message);
  if (messageError) errors.message = messageError;
  
  const fileError = validateFile(formData.file || null);
  if (fileError) errors.file = fileError;
  
  return errors;
};

export const isFormValid = (errors: FormErrors): boolean => {
  return Object.keys(errors).length === 0;
};
